import { useEffect, useState, useCallback } from "react";
import {
  adminLogin,
  getInquiries,
  updateInquiry,
  deleteInquiry,
  getAllEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  getSummary,
  getSettings,
  updateSettings,
  changePassword,
} from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Trash2, Plus, Mail, Phone, LogOut, Loader2, LayoutDashboard, Inbox,
  CalendarDays, Settings as SettingsIcon, KeyRound, Save, CalendarClock,
} from "lucide-react";

const STATUS = ["new", "contacted", "booked", "completed"];
const statusColor = {
  new: "bg-rgv-red",
  contacted: "bg-yellow-600",
  booked: "bg-blue-600",
  completed: "bg-green-600",
};

const emptyEvent = { title: "", date: "", time: "", location: "", game: "", description: "", published: true };

export default function Admin() {
  const { refresh: refreshSettings } = useSettings();
  const [pw, setPw] = useState(sessionStorage.getItem("rgv_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [summary, setSummary] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [events, setEvents] = useState([]);
  const [noteDrafts, setNoteDrafts] = useState({});

  const [evtForm, setEvtForm] = useState(emptyEvent);
  const [editingId, setEditingId] = useState(null);
  const [evtOpen, setEvtOpen] = useState(false);

  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const loadData = useCallback(async (token) => {
    try {
      const [sum, inq, evs, st] = await Promise.all([
        getSummary(token), getInquiries(token), getAllEvents(token), getSettings(),
      ]);
      setSummary(sum);
      setInquiries(inq);
      setNoteDrafts(Object.fromEntries(inq.map((i) => [i.id, i.notes || ""])));
      setEvents(evs);
      setSettingsForm(st);
    } catch {
      toast.error("Session expired. Please log in again.");
      logout();
    }
  }, []);

  useEffect(() => {
    if (pw) {
      getSummary(pw)
        .then(() => { setAuthed(true); loadData(pw); })
        .catch(() => { setAuthed(false); sessionStorage.removeItem("rgv_admin_token"); });
    }
  }, [pw, loadData]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminLogin(emailInput, pwInput);
      sessionStorage.setItem("rgv_admin_token", token);
      setPw(token);
      setAuthed(true);
      await loadData(token);
      toast.success("Welcome back.");
    } catch (err) {
      const msg = err?.response?.status === 429
        ? err.response.data.detail
        : "Invalid email or password.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  function logout() {
    sessionStorage.removeItem("rgv_admin_token");
    setPw(""); setAuthed(false); setPwInput(""); setEmailInput("");
  }

  const changeStatus = async (id, status) => {
    try {
      await updateInquiry(id, { status }, pw);
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, status } : i)));
      getSummary(pw).then(setSummary).catch(() => {});
    } catch { toast.error("Update failed."); }
  };

  const saveNotes = async (id) => {
    try {
      await updateInquiry(id, { notes: noteDrafts[id] || "" }, pw);
      setInquiries((list) => list.map((i) => (i.id === id ? { ...i, notes: noteDrafts[id] } : i)));
      toast.success("Notes saved.");
    } catch { toast.error("Could not save notes."); }
  };

  const removeInquiry = async (id) => {
    try {
      await deleteInquiry(id, pw);
      setInquiries((list) => list.filter((i) => i.id !== id));
      getSummary(pw).then(setSummary).catch(() => {});
      toast.success("Inquiry deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const openNewEvent = () => { setEvtForm(emptyEvent); setEditingId(null); setEvtOpen(true); };
  const openEditEvent = (ev) => { setEvtForm(ev); setEditingId(ev.id); setEvtOpen(true); };

  const saveEvent = async (e) => {
    e.preventDefault();
    if (!evtForm.title.trim() || !evtForm.date.trim()) { toast.error("Title and date are required."); return; }
    try {
      if (editingId) {
        const updated = await updateEvent(editingId, evtForm, pw);
        setEvents((list) => list.map((ev) => (ev.id === editingId ? updated : ev)));
      } else {
        const created = await createEvent(evtForm, pw);
        setEvents((list) => [...list, created]);
      }
      setEvtOpen(false);
      getSummary(pw).then(setSummary).catch(() => {});
      toast.success("Event saved.");
    } catch { toast.error("Save failed."); }
  };

  const removeEvent = async (id) => {
    try {
      await deleteEvent(id, pw);
      setEvents((list) => list.filter((e) => e.id !== id));
      getSummary(pw).then(setSummary).catch(() => {});
      toast.success("Event deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const setEvt = (k) => (v) => setEvtForm((f) => ({ ...f, [k]: v }));

  // ----- Settings -----
  const setContact = (k) => (v) =>
    setSettingsForm((s) => ({ ...s, contact: { ...s.contact, [k]: v } }));
  const setPkg = (i, k) => (v) =>
    setSettingsForm((s) => {
      const packages = s.packages.map((p, idx) => (idx === i ? { ...p, [k]: v } : p));
      return { ...s, packages };
    });

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        contact: settingsForm.contact,
        serviceArea: settingsForm.serviceArea,
        packages: settingsForm.packages.map((p) => ({
          ...p,
          includes: Array.isArray(p.includes)
            ? p.includes
            : String(p.includes).split("\n").map((x) => x.trim()).filter(Boolean),
        })),
      };
      await updateSettings(payload, pw);
      await refreshSettings();
      toast.success("Settings saved — live on the site.");
    } catch { toast.error("Could not save settings."); }
    finally { setSavingSettings(false); }
  };

  // ----- Password -----
  const savePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match."); return; }
    setSavingPw(true);
    try {
      await changePassword(pwForm.current, pwForm.next, pw);
      toast.success("Password changed. Please log in again.");
      setPwForm({ current: "", next: "", confirm: "" });
      setTimeout(logout, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password.");
    } finally { setSavingPw(false); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-rgv-black flex items-center justify-center px-4" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-rgv-darkred rounded-2xl bg-[#111] p-8">
          <h1 className="font-display uppercase italic text-3xl mb-1">RGV <span className="text-rgv-red">Admin</span></h1>
          <p className="text-rgv-gray text-sm mb-6">Sign in to continue.</p>
          <Label htmlFor="admin-email" className="text-rgv-gray">Email</Label>
          <Input id="admin-email" type="email" inputMode="email" autoComplete="email" data-testid="admin-email-input" value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            className="bg-black border-rgv-maroon text-white mt-1 mb-4 h-12" placeholder="you@rgvgaming.com" />
          <Label htmlFor="pw" className="text-rgv-gray">Password</Label>
          <Input id="pw" type="password" autoComplete="current-password" data-testid="admin-password-input" value={pwInput}
            onChange={(e) => setPwInput(e.target.value)}
            className="bg-black border-rgv-maroon text-white mt-1 mb-6 h-12" />
          <button type="submit" data-testid="admin-login-btn" disabled={loading}
            className="btn-skew btn-red w-full py-3 disabled:opacity-60">
            <span className="flex items-center gap-2">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Log In</span>
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rgv-black text-white" data-testid="admin-dashboard">
      <div className="border-b border-rgv-maroon">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <h1 className="font-display uppercase italic text-xl sm:text-3xl">RGV Gaming <span className="text-rgv-red">Control Room</span></h1>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-rgv-gray hover:text-white">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="bg-[#111] border border-rgv-maroon flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1.5" />Dashboard</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries"><Inbox className="h-4 w-4 mr-1.5" />Inquiries ({inquiries.length})</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events"><CalendarDays className="h-4 w-4 mr-1.5" />Events ({events.length})</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><SettingsIcon className="h-4 w-4 mr-1.5" />Content</TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password"><KeyRound className="h-4 w-4 mr-1.5" />Password</TabsTrigger>
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard" className="mt-6" data-testid="dashboard-panel">
            {summary && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    ["New Inquiries", summary.counts.new_inquiries, "text-rgv-red"],
                    ["Total Inquiries", summary.counts.total_inquiries, "text-white"],
                    ["Upcoming Events", summary.counts.upcoming_events, "text-blue-400"],
                    ["Total Events", summary.counts.total_events, "text-white"],
                  ].map(([label, val, cls]) => (
                    <div key={label} className="border border-rgv-maroon rounded-xl bg-[#111] p-5">
                      <div className={`font-display text-4xl ${cls}`}>{val}</div>
                      <div className="text-rgv-gray uppercase text-xs mt-1 font-display tracking-wide">{label}</div>
                    </div>
                  ))}
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  <div>
                    <h3 className="font-display uppercase text-xl mb-3 flex items-center gap-2"><Inbox className="h-5 w-5 text-rgv-red" /> New Inquiries</h3>
                    {summary.recent_new_inquiries.length === 0 ? (
                      <p className="text-rgv-gray text-sm">No new inquiries.</p>
                    ) : (
                      <div className="space-y-2">
                        {summary.recent_new_inquiries.map((i) => (
                          <div key={i.id} data-testid={`dash-inquiry-${i.id}`} className="border border-rgv-maroon rounded-lg bg-[#111] p-3">
                            <div className="flex justify-between">
                              <span className="font-display uppercase">{i.name}</span>
                              <a href={`tel:${i.phone}`} className="text-rgv-gray text-sm hover:text-white">{i.phone}</a>
                            </div>
                            <p className="text-rgv-gray text-sm">{i.package || i.event_type || "—"} {i.preferred_date && `· ${i.preferred_date}`}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-display uppercase text-xl mb-3 flex items-center gap-2"><CalendarClock className="h-5 w-5 text-rgv-red" /> Upcoming Events</h3>
                    {summary.upcoming_events.length === 0 ? (
                      <p className="text-rgv-gray text-sm">No upcoming events.</p>
                    ) : (
                      <div className="space-y-2">
                        {summary.upcoming_events.map((e) => (
                          <div key={e.id} data-testid={`dash-event-${e.id}`} className="border border-rgv-maroon rounded-lg bg-[#111] p-3">
                            <span className="font-display uppercase">{e.title}</span>
                            <p className="text-rgv-gray text-sm">{e.date} {e.time && `· ${e.time}`}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* INQUIRIES */}
          <TabsContent value="inquiries" className="mt-6">
            {inquiries.length === 0 ? (
              <p className="text-rgv-gray">No inquiries yet.</p>
            ) : (
              <div className="space-y-4">
                {inquiries.map((inq) => (
                  <div key={inq.id} data-testid={`inquiry-${inq.id}`} className="border border-rgv-maroon rounded-xl bg-[#111] p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-display text-xl uppercase">{inq.name}</h3>
                          <Badge className={`${statusColor[inq.status]} text-white capitalize`}>{inq.status}</Badge>
                          {inq.email_sent && <span className="text-xs text-green-500 flex items-center gap-1"><Mail className="h-3 w-3" /> alerted</span>}
                        </div>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-rgv-gray">
                          <a href={`tel:${inq.phone}`} className="flex items-center gap-1 hover:text-white"><Phone className="h-3.5 w-3.5 text-rgv-red" /> {inq.phone}</a>
                          {inq.email && <a href={`mailto:${inq.email}`} className="flex items-center gap-1 hover:text-white"><Mail className="h-3.5 w-3.5 text-rgv-red" /> {inq.email}</a>}
                        </div>
                        <div className="grid sm:grid-cols-3 gap-2 mt-3 text-sm text-rgv-gray">
                          {inq.package && <span><span className="text-white/60">Package:</span> {inq.package}</span>}
                          {inq.event_type && <span><span className="text-white/60">Type:</span> {inq.event_type}</span>}
                          {inq.preferred_date && <span><span className="text-white/60">Date:</span> {inq.preferred_date}</span>}
                        </div>
                        {inq.message && <p className="text-rgv-gray mt-2 text-sm italic">&ldquo;{inq.message}&rdquo;</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={inq.status} onValueChange={(v) => changeStatus(inq.id, v)}>
                          <SelectTrigger data-testid={`inquiry-status-${inq.id}`} className="w-36 bg-black border-rgv-maroon text-white h-11">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-rgv-maroon text-white">
                            {STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button onClick={() => removeInquiry(inq.id)} data-testid={`inquiry-delete-${inq.id}`} className="p-2 text-rgv-gray hover:text-rgv-red">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label className="text-rgv-gray text-xs uppercase tracking-wide">Staff Notes</Label>
                      <div className="flex gap-2 mt-1">
                        <Textarea
                          data-testid={`inquiry-notes-${inq.id}`}
                          value={noteDrafts[inq.id] ?? ""}
                          onChange={(e) => setNoteDrafts((d) => ({ ...d, [inq.id]: e.target.value }))}
                          className="bg-black border-rgv-maroon text-white text-sm"
                          rows={2}
                          placeholder="Follow-up details, quotes, deposit status..."
                        />
                        <button onClick={() => saveNotes(inq.id)} data-testid={`inquiry-notes-save-${inq.id}`}
                          className="btn-skew btn-outline-red px-4 shrink-0"><span>Save</span></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={evtOpen} onOpenChange={setEvtOpen}>
                <DialogTrigger asChild>
                  <button onClick={openNewEvent} data-testid="admin-add-event-btn" className="btn-skew btn-red px-5 py-2.5">
                    <span className="flex items-center gap-2"><Plus className="h-4 w-4" /> New Event</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0A0A0A] border-rgv-darkred text-white max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-display uppercase italic text-2xl">{editingId ? "Edit" : "New"} Event</DialogTitle>
                    <DialogDescription className="text-rgv-gray">Fill in the event details. Published events appear on the public site.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={saveEvent} className="space-y-4 mt-2">
                    <div>
                      <Label className="text-rgv-gray">Title *</Label>
                      <Input data-testid="event-title-input" value={evtForm.title} onChange={(e) => setEvt("title")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-rgv-gray">Date *</Label><Input type="date" data-testid="event-date-input" value={evtForm.date} onChange={(e) => setEvt("date")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" /></div>
                      <div><Label className="text-rgv-gray">Time</Label><Input data-testid="event-time-input" value={evtForm.time} onChange={(e) => setEvt("time")(e.target.value)} placeholder="6:00 PM" className="bg-black border-rgv-maroon text-white mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label className="text-rgv-gray">Location</Label><Input data-testid="event-location-input" value={evtForm.location} onChange={(e) => setEvt("location")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" /></div>
                      <div><Label className="text-rgv-gray">Game</Label><Input data-testid="event-game-input" value={evtForm.game} onChange={(e) => setEvt("game")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" /></div>
                    </div>
                    <div><Label className="text-rgv-gray">Description</Label><Textarea data-testid="event-description-input" value={evtForm.description} onChange={(e) => setEvt("description")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" rows={3} /></div>
                    <label className="flex items-center gap-2 text-rgv-gray">
                      <input type="checkbox" data-testid="event-published-checkbox" checked={evtForm.published} onChange={(e) => setEvt("published")(e.target.checked)} className="accent-[#D02129] h-4 w-4" />
                      Published (visible on public site)
                    </label>
                    <button type="submit" data-testid="event-save-btn" className="btn-skew btn-red w-full py-3"><span>Save Event</span></button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {events.length === 0 ? (
              <p className="text-rgv-gray">No events yet. Add your first tournament or game night.</p>
            ) : (
              <div className="space-y-3">
                {events.map((ev) => (
                  <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="border border-rgv-maroon rounded-xl bg-[#111] p-4 flex items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-display text-lg uppercase">{ev.title}</h3>
                        {!ev.published && <Badge className="bg-neutral-600 text-white">Draft</Badge>}
                      </div>
                      <p className="text-sm text-rgv-gray mt-1">{ev.date} {ev.time && `· ${ev.time}`} {ev.location && `· ${ev.location}`} {ev.game && `· ${ev.game}`}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => openEditEvent(ev)} data-testid={`admin-event-edit-${ev.id}`} className="text-sm font-display uppercase text-rgv-gray hover:text-white px-2">Edit</button>
                      <button onClick={() => removeEvent(ev.id)} data-testid={`admin-event-delete-${ev.id}`} className="p-2 text-rgv-gray hover:text-rgv-red"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS / CONTENT */}
          <TabsContent value="settings" className="mt-6" data-testid="settings-panel">
            {settingsForm && (
              <div className="space-y-8">
                <section>
                  <h3 className="font-display uppercase text-2xl mb-4">Contact & Hours</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      ["phone", "Phone"], ["email", "Email"], ["city", "City"],
                      ["area", "Service Area (headline)"], ["hours", "Hours"], ["fleet", "Fleet / Equipment line"],
                    ].map(([k, label]) => (
                      <div key={k}>
                        <Label className="text-rgv-gray">{label}</Label>
                        <Input data-testid={`settings-${k}`} value={settingsForm.contact?.[k] || ""}
                          onChange={(e) => setContact(k)(e.target.value)}
                          className="bg-black border-rgv-maroon text-white mt-1" />
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="font-display uppercase text-2xl mb-2">Service-Area Cities</h3>
                  <p className="text-rgv-gray text-sm mb-2">Comma-separated. Shown in the footer.</p>
                  <Input data-testid="settings-service-area" value={(settingsForm.serviceArea || []).join(", ")}
                    onChange={(e) => setSettingsForm((s) => ({ ...s, serviceArea: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))}
                    className="bg-black border-rgv-maroon text-white" />
                </section>

                <section>
                  <h3 className="font-display uppercase text-2xl mb-4">Package Pricing</h3>
                  <div className="space-y-4">
                    {settingsForm.packages.map((p, i) => (
                      <div key={i} data-testid={`settings-package-${i}`} className="border border-rgv-maroon rounded-xl bg-[#111] p-4 grid sm:grid-cols-12 gap-3 items-start">
                        <div className="sm:col-span-5">
                          <Label className="text-rgv-gray text-xs">Name</Label>
                          <Input value={p.name} onChange={(e) => setPkg(i, "name")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" />
                        </div>
                        <div className="sm:col-span-4">
                          <Label className="text-rgv-gray text-xs">Price</Label>
                          <Input data-testid={`settings-package-price-${i}`} value={p.price} onChange={(e) => setPkg(i, "price")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" placeholder="$395 or Contact for pricing" />
                        </div>
                        <div className="sm:col-span-3">
                          <Label className="text-rgv-gray text-xs">Ages</Label>
                          <Input value={p.ages} onChange={(e) => setPkg(i, "ages")(e.target.value)} className="bg-black border-rgv-maroon text-white mt-1" />
                        </div>
                        <div className="sm:col-span-12">
                          <Label className="text-rgv-gray text-xs">Includes (one per line)</Label>
                          <Textarea
                            value={Array.isArray(p.includes) ? p.includes.join("\n") : p.includes}
                            onChange={(e) => setPkg(i, "includes")(e.target.value.split("\n"))}
                            className="bg-black border-rgv-maroon text-white mt-1" rows={4} />
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <button onClick={saveSettings} data-testid="settings-save-btn" disabled={savingSettings}
                  className="btn-skew btn-red px-8 py-3 disabled:opacity-60">
                  <span className="flex items-center gap-2">
                    {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Content
                  </span>
                </button>
              </div>
            )}
          </TabsContent>

          {/* PASSWORD */}
          <TabsContent value="password" className="mt-6" data-testid="password-panel">
            <form onSubmit={savePassword} className="max-w-sm space-y-4 border border-rgv-maroon rounded-xl bg-[#111] p-6">
              <h3 className="font-display uppercase text-2xl">Change Password</h3>
              <div>
                <Label className="text-rgv-gray">Current Password</Label>
                <Input type="password" data-testid="pw-current" value={pwForm.current} onChange={(e) => setPwForm((f) => ({ ...f, current: e.target.value }))} className="bg-black border-rgv-maroon text-white mt-1" />
              </div>
              <div>
                <Label className="text-rgv-gray">New Password (min 8 chars)</Label>
                <Input type="password" data-testid="pw-new" value={pwForm.next} onChange={(e) => setPwForm((f) => ({ ...f, next: e.target.value }))} className="bg-black border-rgv-maroon text-white mt-1" />
              </div>
              <div>
                <Label className="text-rgv-gray">Confirm New Password</Label>
                <Input type="password" data-testid="pw-confirm" value={pwForm.confirm} onChange={(e) => setPwForm((f) => ({ ...f, confirm: e.target.value }))} className="bg-black border-rgv-maroon text-white mt-1" />
              </div>
              <button type="submit" data-testid="pw-save-btn" disabled={savingPw} className="btn-skew btn-red w-full py-3 disabled:opacity-60">
                <span className="flex items-center gap-2">{savingPw && <Loader2 className="h-4 w-4 animate-spin" />} Update Password</span>
              </button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
